/**
 * @voltxampere/engine — MCU execution DC stamp (Sprint 47-2)
 *
 * Sprint 46-3 shipped infrastructure ONLY (stamp scaffold no-op + AVR
 * emulator + Intel HEX parser). Sprint 47-2 wires the emulator into
 * the stamp evaluate body — output GPIO pin (PB5 MVP) drives an
 * augmented MNA voltage-source row whose value is read from the
 * emulator's PORTB register.
 *
 * Sprint 47-2 MVP scope (Şefim onaylı Yol B alt-sprint split):
 *
 *   - Single PB5 output drive (Arduino UNO LED_BUILTIN). Other 7
 *     Port B pins ignored — Sprint 48 (Duyu Açılışı) extends to
 *     full 8-pin output drive when emulator state machine for the
 *     ADC + interrupt vectors is wired in.
 *   - Per-component AVR state in a module-level `Map<componentId,
 *     AvrState>`. Lazy-created on first stamp call (parses
 *     `flashHex` via `parseIntelHex`, calls `loadFlashBytes`).
 *   - DC analysis is **read-only** wrt emulator state. The stamp
 *     does NOT execute instructions — Newton-Raphson iteration
 *     would otherwise non-converge as the boolean output flips
 *     mid-iter. Instead, the test runner / Inspector runtime panel
 *     calls `stepN(getMcuState(componentId), count)` between solves.
 *   - PB0 input voltage threshold = `vSupply / 2` (Sprint 41
 *     digital-gate pattern reuse).
 *
 * Engine zero-diff cumulative invariant Sprint 46 baseline'dan
 * (`227f99d`) intact. New file behaviour additive — no existing
 * stamp's body changes. 22 mevcut frozen sentinel bit-identical
 * (none of them contain MCU components).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { defineDCStamp } from "./definition.js";
import { getTerminalNetId, getNumericParam } from "./terminal-utils.js";
import { createAvrState, loadFlashBytes, getGpioOutput, setGpioInput, } from "../mcu/avr-emulator.js";
import { parseIntelHex } from "../mcu/hex-parser.js";
/**
 * Module-level emulator state map. Keyed by `component.id` so each
 * MCU instance keeps its own program counter / register file / SRAM
 * across solver calls. Cleared via `clearAllMcuStates()` between
 * test runs (Sprint 47-3 sentinel runner uses this).
 */
const mcuStates = new Map();
/** Family-driven flash + RAM defaults (mirrors components/types/mcu-execution.ts). */
const FAMILY_SIZES = {
    atmega328p: { flashWords: 16384, sramBytes: 2048 },
    atmega2560: { flashWords: 131072, sramBytes: 8192 },
    attiny85: { flashWords: 4096, sramBytes: 512 },
};
/**
 * Lazy-create or fetch the per-component emulator state. The stamp
 * never throws on missing flash — an empty `flashHex` parses to a
 * zero-byte `Uint8Array` and the AVR halts gracefully on the first
 * unknown opcode (most likely the all-zero word at PC=0 = NOP, then
 * eventually a 0x0001 reserved word that triggers `state.halted`).
 */
function getOrCreateMcuState(componentId, family, flashHex) {
    const existing = mcuStates.get(componentId);
    if (existing !== undefined)
        return existing;
    const sizes = FAMILY_SIZES[family];
    const state = createAvrState(sizes.flashWords, sizes.sramBytes);
    if (flashHex.trim().length > 0) {
        try {
            const parsed = parseIntelHex(flashHex);
            loadFlashBytes(state, parsed.bytes);
        }
        catch {
            // Malformed .hex — leave flash zero-filled (NOP loop until halt).
            // Inspector runtime panel surfaces parse errors at upload time.
        }
    }
    mcuStates.set(componentId, state);
    return state;
}
/**
 * Test + UI helper — clear all per-component emulator state.
 * Sentinel test runner calls this in `beforeEach` so each test
 * starts with fresh emulator state.
 */
export function clearAllMcuStates() {
    mcuStates.clear();
}
/**
 * Test + UI helper — fetch the emulator state for a given component.
 * Sentinel runner uses this to call `stepN(...)` between DC solves
 * (Sprint 47-3 pattern: load flash → step N → solve → assert PB5).
 */
export function getMcuState(componentId) {
    return mcuStates.get(componentId);
}
/**
 * Sprint 47-2 stamp body. MVP scope: single PB5 output drive +
 * single PB0 input read. Sprint 48 extends to full Port B / C / D +
 * ADC + PWM.
 *
 * Algorithm:
 *
 *   1. Resolve component params → family, flashHex, vSupply.
 *   2. Lazy-create AVR state (parse flash on first call).
 *   3. Read PB0 input voltage from xPrev → write to emulator's
 *      input register via `setGpioInput`.
 *   4. Read PB5 output from emulator via `getGpioOutput`.
 *   5. If PB5 is configured as output (DDRB bit 5 = 1), stamp the
 *      augmented MNA voltage-source row driving the PB5 net to
 *      `vSupply` (HIGH) or 0 V (LOW).
 *   6. If PB5 is configured as input (DDRB bit 5 = 0), zero out the
 *      augmented row's RHS so the unused branch current settles to
 *      0 (planner allocates the slot statically — we cannot opt out
 *      from declaring `["vsource"]` augments without re-architecting
 *      the planner's per-component-type slot pre-allocation).
 */
export const mcuExecutionDCStamp = (ctx, component) => {
    const params = component.params;
    const family = params.family ?? "atmega328p";
    const flashHex = params.flashHex ?? "";
    const vSupply = getNumericParam(component, "vSupply", 5);
    const state = getOrCreateMcuState(component.id, family, flashHex);
    // 1. Read PB0 input from xPrev → emulator input register.
    // Threshold = vSupply / 2 (Sprint 41 digital-gate convention).
    const pb0Idx = ctx.nodeIndex(getTerminalNetId(component, "pb0"));
    if (pb0Idx >= 0) {
        const vPb0 = ctx.xPrev[pb0Idx] ?? 0;
        setGpioInput(state, "B", 0, vPb0 >= vSupply / 2);
    }
    // 2. Resolve PB5 augmented row (always allocated by planner).
    const pb5Idx = ctx.nodeIndex(getTerminalNetId(component, "pb5"));
    const k = ctx.vSourceIndex(component.id);
    // 3. Read PB5 output state. `null` → input mode (DDRB bit 5 = 0).
    const pb5Output = getGpioOutput(state, "B", 5);
    if (pb5Output === null) {
        // Input mode — make the augmented row a 0-V "passthrough" so
        // the planner-allocated slot stays well-conditioned. Pattern:
        // identity row at [k][k] = 1 with rhs[k] = 0 → branch current 0,
        // no node coupling.
        ctx.addY(k, k, 1);
        return;
    }
    // 4. Output mode — stamp augmented voltage source on PB5 net.
    if (pb5Idx < 0)
        return; // PB5 connected to ground — degenerate
    const outputVoltage = pb5Output ? vSupply : 0;
    ctx.addY(pb5Idx, k, 1);
    ctx.addY(k, pb5Idx, 1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + outputVoltage * ctx.sourceScale;
};
/**
 * MCU execution carries one augmented MNA row in Sprint 47-2 (PB5
 * output drive, single pin). Sprint 48 extends to multi-pin: each
 * configured-output Port B/C/D pin gets its own augmented row via
 * a dynamic vSourceIndex pattern (planner pre-allocates 8-row block
 * per MCU instance, stamp activates only the rows whose DDRx bit
 * is set output).
 */
export const mcuExecutionDCStampDefinition = defineDCStamp("mcu-execution", mcuExecutionDCStamp, [
    "vsource",
]);
//# sourceMappingURL=mcu-execution.js.map